import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllRoleplays,
  fetchRoleplay,
  fetchRoleplayHistory,
  updateRoleplayControlGraph,
  fetchRoleplayLinks,
  createRoleplayLink,
  updateRoleplayLink,
  fetchRoleplayAssessedSessions,
  RoleplayResponse,
  RoleplayLink,
  RoleplayAssessedSession,
} from "../actions/roleplayActions";

interface RoleplayState {
  roleplays: RoleplayResponse[];
  currentRoleplay: RoleplayResponse | null;
  roleplayHistory: RoleplayResponse[];
  roleplayLinks: { [roleplayId: string]: RoleplayLink[] };
  roleplayAssessedSessions: RoleplayAssessedSession[];
  loading: boolean;
  historyLoading: boolean;
  linksLoading: boolean;
  sessionsLoading: boolean;
  error: string | null;
}

const initialState: RoleplayState = {
  roleplays: [],
  currentRoleplay: null,
  roleplayHistory: [],
  roleplayLinks: {},
  roleplayAssessedSessions: [],
  loading: false,
  historyLoading: false,
  linksLoading: false,
  sessionsLoading: false,
  error: null,
};

const roleplaySlice = createSlice({
  name: "roleplays",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch all roleplays
    builder.addCase(fetchAllRoleplays.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAllRoleplays.fulfilled, (state, action) => {
      state.loading = false;
      state.roleplays = action.payload;
    });
    builder.addCase(fetchAllRoleplays.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch roleplays";
    });

    // Fetch single roleplay
    builder.addCase(fetchRoleplay.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchRoleplay.fulfilled, (state, action) => {
      state.loading = false;
      state.currentRoleplay = action.payload;
    });
    builder.addCase(fetchRoleplay.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch roleplay";
    });

    // Fetch roleplay history
    builder.addCase(fetchRoleplayHistory.pending, (state) => {
      state.historyLoading = true;
      state.error = null;
    });
    builder.addCase(fetchRoleplayHistory.fulfilled, (state, action) => {
      state.historyLoading = false;
      state.roleplayHistory = action.payload;
    });
    builder.addCase(fetchRoleplayHistory.rejected, (state, action) => {
      state.historyLoading = false;
      state.error = action.error.message || "Failed to fetch roleplay history";
    });

    // Update roleplay control graph
    builder.addCase(updateRoleplayControlGraph.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateRoleplayControlGraph.fulfilled, (state, action) => {
      state.loading = false;
      state.currentRoleplay = action.payload;
    });
    builder.addCase(updateRoleplayControlGraph.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to update control graph";
    });

    // Fetch roleplay links
    builder.addCase(fetchRoleplayLinks.pending, (state) => {
      state.linksLoading = true;
      state.error = null;
    });
    builder.addCase(fetchRoleplayLinks.fulfilled, (state, action) => {
      state.linksLoading = false;
      const roleplayId = action.meta.arg.roleplayId;
      state.roleplayLinks[roleplayId] = action.payload;
    });
    builder.addCase(fetchRoleplayLinks.rejected, (state, action) => {
      state.linksLoading = false;
      state.error = action.error.message || "Failed to fetch roleplay links";
    });

    // Create roleplay link
    builder.addCase(createRoleplayLink.pending, (state) => {
      state.linksLoading = true;
      state.error = null;
    });
    builder.addCase(createRoleplayLink.fulfilled, (state, action) => {
      state.linksLoading = false;
      // Refetch links after creation
    });
    builder.addCase(createRoleplayLink.rejected, (state, action) => {
      state.linksLoading = false;
      state.error = action.error.message || "Failed to create roleplay link";
    });

    // Update roleplay link
    builder.addCase(updateRoleplayLink.pending, (state) => {
      state.linksLoading = true;
      state.error = null;
    });
    builder.addCase(updateRoleplayLink.fulfilled, (state, action) => {
      state.linksLoading = false;
      // Refetch links after update
    });
    builder.addCase(updateRoleplayLink.rejected, (state, action) => {
      state.linksLoading = false;
      state.error = action.error.message || "Failed to update roleplay link";
    });

    // Fetch roleplay assessed sessions
    builder.addCase(fetchRoleplayAssessedSessions.pending, (state) => {
      state.sessionsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchRoleplayAssessedSessions.fulfilled, (state, action) => {
      state.sessionsLoading = false;
      state.roleplayAssessedSessions = action.payload;
    });
    builder.addCase(fetchRoleplayAssessedSessions.rejected, (state, action) => {
      state.sessionsLoading = false;
      state.error =
        action.error.message || "Failed to fetch roleplay assessed sessions";
    });
  },
});

export default roleplaySlice.reducer;
